import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * The money formatter — use this rather than hand-rolling one.
 *
 * Both display bugs on the artist page came from a page-local `$` +
 * toLocaleString copy, which is a worse shape than this one in two ways:
 *   • its maximum defaults to 3, so commission (earnings x rate) rendered
 *     $2,571.269 until #774 capped it
 *   • a minimum of 0 dropped the trailing cent, so $3,082.30 rendered
 *     $3,082.3 until #825 pinned it
 *   • it puts the sign after the symbol ($-145.50); this gives -$145.50
 *
 * `style: 'currency'` already defaults to 2 digits for AUD, so the explicit
 * options below are belt-and-braces — the real fix is that every surface calls
 * THIS instead of its own copy. Covered by tests/lib/formatCurrency.test.ts;
 * if $145.00 looks noisy to you, that test records why it stays.
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a date for display. Strips any timezone suffix before parsing so
 * a value like "2026-03-25 17:30:00+00" (which the events table uses to
 * store local wall-clock time with a fake +00 offset) renders as 25 Mar,
 * not 26 Mar after a browser-local TZ shift. Real ISO-UTC inputs render
 * the same as before when interpreted as local wall-clock components.
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string'
    ? new Date(stripOffsetIfAfterTime(date))
    : date;
  return d.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Only strip a trailing Z / ±HH:MM when there's a time component before it.
// A plain date like "2026-03-25" must be left alone — the "-25" looks like
// an offset to a naive regex but it's actually the day.
function stripOffsetIfAfterTime(s: string): string {
  if (!s.includes(':')) return s; // no time → no TZ to strip
  return s.replace(/(Z|[+-]\d{2}:?\d{0,2})\s*$/i, '');
}

/**
 * Parse a platform "local wall-clock tagged UTC" timestamp (e.g.
 * events.event_date / session_start_local = "2026-07-30 19:00:00+00" meaning
 * 7pm AT THE VENUE) for DISPLAY: strips the bogus offset so date-fns/Intl
 * render the same wall date/time in every viewer's timezone. Parsing it as a
 * real instant shifts Australian viewers past midnight (a Brisbane 30 Jul 7pm
 * show read "31 Jul" from Perth).
 */
export function parseWallClock(date: string | Date | null | undefined): Date {
  if (date instanceof Date) return date;
  if (typeof date === 'string') return new Date(stripOffsetIfAfterTime(date));
  // null / undefined / anything else → Invalid Date. Previously this returned
  // the value as-is, so a null event_date became `undefined`, and callers doing
  // `parseWallClock(x).toDateString()` crashed with "Cannot read properties of
  // undefined (reading 'toDateString')" — which threw inside the org-events
  // combine and left the whole events list stuck on "No events found".
  return new Date(NaN);
}
